/**
 * context menu
 * @constructor
 * @param {string} contextElementId - Id of the context Element
 */

/**
 * add this link/css to html file
 * <link rel="stylesheet" type="text/css" href="/WebInterface/new-ui/modules/contextmenu/contextmenu.css" />
 */

import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import {
  buttonsToIgnoreForBasket,
  fileTypes,
} from "/WebInterface/new-ui/assets/js/constants.js";
import {
  slideShowAllowedFilesExtensions,
  mediaTypes,
  editInlineFilesExtensionAllowed,
} from "/WebInterface/new-ui/assets/js/constants.js";
import menubar from "/WebInterface/new-ui/assets/js/app/components/menubar.js";

export default class ContextMenu {
  constructor({ contextElementId }) {
    this.contextElementId = contextElementId;
  }

  /**
   * Initializes the context menu.
   * @param {Object} options - Options for the context menu.
   * @param {array} options.contextMenuItems - Array of items to be rendered in the context menu.
   * @param {function} [options.onBeforeShow=()=>{}] - Callback function to be executed before showing the context menu.
   * @param {function} [options.onAfterShow=()=>{}] - Callback function to be executed after showing the context menu.
   * @param {function} [options.onBeforeHide=()=>{}] - Callback function to be executed before hiding the context menu.
   * @param {function} [options.onAfterHide=()=>{}] - Callback function to be executed after hiding the context menu.
   * @param {function} [options.onClick=()=>{}] - Callback function to be executed when an item in the context menu is clicked.
   */
  init = ({
    contextMenuItems,
    onBeforeShow = () => {},
    onAfterShow = () => {},
    onBeforeHide = () => {},
    onAfterHide = () => {},
    onClick = () => {},
  }) => {
    this.contextMenuItems = contextMenuItems;
    this.contextElement = document.getElementById(this.contextElementId);

    // Events
    this.onBeforeShow = onBeforeShow;
    this.onAfterShow = onAfterShow;
    this.onBeforeHide = onBeforeHide;
    this.onAfterHide = onAfterHide;
    this.onClick = onClick;

    if (!this.contextElement) {
      this.contextElement = document.createElement("div");
      this.contextElement.id = this.contextElementId;
      this.contextElement.classList.add("context-menu-section");
      document.body.append(this.contextElement);
    }

    if (this.closeMenu) {
      this.closeMenu();
    }

    this.contextElement.innerHTML = "";

    const contextMenuContainer = document.createElement("div");
    contextMenuContainer.classList.add("context-menu-container");

    const createContextItem = ({ item }) => {
      let iconContainer = null;
      if (item?.customImageUrl) {
        iconContainer = document.createElement("w-image");
        iconContainer.classList.add("mr-2", "custom-icon");
        iconContainer.setAttribute("alt", "custom-icon");
        iconContainer.setAttribute("theme-specific", "true");
        iconContainer.src = `/WebInterface/images/${item.customImageUrl}`;
      } else {
        iconContainer = document.createElement("i");
        iconContainer.classList.add("mr-2", item.iconClass);
      }

      const title = document.createElement("p");

      if (_$("html").attr("lang") !== "en" || !item.hotKeyHTML) {
        title.setAttribute("data-i18n-key", item.i18nkey);
        title.innerText = t(item.i18nkey);
      } else {
        title.innerHTML = item.hotKeyHTML;
      }

      const contextMenuItem = document.createElement("div");
      contextMenuItem.classList.add("context-menu-item");
      if (item.hasBorder) {
        contextMenuItem.classList.add("border-b");
      }
      contextMenuItem.append(iconContainer, title);

      if (item.subMenu) {
        const subMenuIcon = document.createElement("i");
        subMenuIcon.classList.add("ml-2", "icon-caret-right");

        contextMenuItem.append(subMenuIcon);
      }

      contextMenuItem.addEventListener("click", () => {
        this.onClick(item, this.item);
        _$(`#${this.contextElementId}`).css(
          null,
          null,
          "opacity: 0; transform: scale(0);"
        );
        this.contextElement.innerHTML = "";
      });

      if (item.eventListener) {
        contextMenuItem.addEventListener("click", item.eventListener);
      }

      return contextMenuItem;
    };

    this.contextElement.append(contextMenuContainer);

    if (this.contextSubMenuContainer) {
      this.contextSubMenuContainer.remove();
    }

    this.contextSubMenuContainer = document.createElement("div");
    this.contextSubMenuContainer.className = "context-sub-menu-container";

    this.mouseEnterOnSubMenu = () => {
      if (this._destroyTimer) {
        clearTimeout(this._destroyTimer);
      }

      if (this._destroySubMenuTimer) {
        clearTimeout(this._destroySubMenuTimer);
      }
    };

    this.contextSubMenuContainer.addEventListener(
      "mouseenter",
      this.mouseEnterOnSubMenu
    );

    this.mouseLeaveOnSubMenu = () => {
      this._destroySubMenuTimer = setTimeout(() => {
        this.closeSubMenu();
      }, 100);

      this._destroyTimer = window.setTimeout(() => {
        this.closeMenu();
      }, 1000);
    };

    this.contextSubMenuContainer.addEventListener(
      "mouseleave",
      this.mouseLeaveOnSubMenu
    );

    _$(this.contextSubMenuContainer).on("click", () => {
      this._destroySubMenuTimer = setTimeout(() => {
        this.closeSubMenu();
      }, 100);
    });

    document.body.append(this.contextSubMenuContainer);

    contextMenuItems.forEach((item) => {
      const createdContextItem = createContextItem({ item });

      contextMenuContainer.append(createdContextItem);

      const renderLevel = (item, createdContextItem) => {
        if (item.subMenu) {
          createdContextItem.addEventListener("mouseenter", () => {
            if (this._destroySubMenuTimer) {
              clearTimeout(this._destroySubMenuTimer);
            }

            createdContextItem.classList.add("background-hover");

            this.contextSubMenuContainer.innerHTML = "";
            item.subMenu.forEach((subItem) => {
              const createdContextSubMenuItem = createContextItem({
                item: subItem,
              });

              createdContextSubMenuItem.addEventListener("mouseenter", () => {
                createdContextItem.classList.add("background-hover");
              });

              createdContextSubMenuItem.addEventListener("mouseleave", () => {
                createdContextItem.classList.remove("background-hover");
              });

              this.contextSubMenuContainer.append(createdContextSubMenuItem);
            });

            this.contextSubMenuContainer.classList.add("is-flex");

            const { left, right, top } =
              createdContextItem.getBoundingClientRect();
            const { normalizedX, normalizedY } = this.normalizePosition(
              right,
              top,
              this.contextSubMenuContainer
            );

            this.contextSubMenuContainer.style.left = `${right}px`;
            this.contextSubMenuContainer.style.top = `${top}px`;
            this.contextSubMenuContainer.style.bottom = "unset";
            this.contextSubMenuContainer.style.transform = "translate(0, 0)";

            if (top !== normalizedY) {
              this.contextSubMenuContainer.style.bottom = "0";
              this.contextSubMenuContainer.style.top = "unset";
            }

            if (right !== normalizedX) {
              this.contextSubMenuContainer.style.left = `${left}px`;
              this.contextSubMenuContainer.style.transform =
                "translateX(-100%)";
            }
          });

          createdContextItem.addEventListener("mouseleave", () => {
            createdContextItem.classList.remove("background-hover");

            this._destroySubMenuTimer = setTimeout(() => {
              this.closeSubMenu();
            }, 100);
          });
        }
      };

      renderLevel(item, createdContextItem);
    });
  };

  /**
   * Handles the context menu event.
   * @param {Event} event - The contextmenu event object.
   * @param {Object} item - The item associated with the context menu.
   */
  handleContextMenu = (event, item) => {
    event.preventDefault();

    this.item = item;
    const contextMenu = this.contextElement;

    //  Normalizing Position Of Context Menu
    const { clientX: mouseX, clientY: mouseY } = event;
    const { normalizedX, normalizedY } = this.normalizePosition(
      mouseX,
      mouseY,
      contextMenu
    );

    const elem = _$(`#${this.contextElementId}`);
    if (this.onBeforeShow) this.onBeforeShow(elem);
    elem.css(
      null,
      null,
      `opacity: 0; transform: scale(0); top: ${normalizedY}px; left: ${normalizedX}px`
    );

    const mouseOut = () => {
      this._destroyTimer = window.setTimeout(() => {
        closeMenu();
      }, 1000);
    };

    const mouseIn = () => {
      if (this._destroyTimer) window.clearTimeout(this._destroyTimer);
    };

    const closeMenu = (this.closeMenu = () => {
      if (this.contextSubMenuContainer)
        this.contextSubMenuContainer.classList.remove("is-flex");

      this.onBeforeHide(elem);
      elem.css(null, null, "opacity: 0; transform: scale(0);");
      this.contextElement.innerHTML = "";
      this.onAfterHide(elem);
      document.body.removeEventListener("keyup", keyupListener);
      elem.off("mouseleave", mouseOut);
      elem.off("mouseenter", mouseIn);
    });

    elem.on("mouseleave", mouseOut);
    elem.on("mouseenter", mouseIn);

    const keyupListener = (event) => {
      let key = event.key || event.keyCode;
      if (key === "Escape" || key === "Esc" || key === 27) {
        closeMenu();
      }
    };

    setTimeout(() => {
      elem.css(null, null, "opacity: 1; transform: scale(1);");
      this.onAfterShow(elem);
      document.body.addEventListener("keyup", keyupListener);
    });

    document.body.addEventListener("click", (e) => {
      if (e.target.offsetParent != contextMenu) {
        closeMenu();
      }
    });
  };

  /**
   * Closes the sub-menu.
   */
  closeSubMenu = () => {
    this.contextSubMenuContainer.removeEventListener(
      "mouseleave",
      this.mouseLeaveOnSubMenu
    );
    this.contextSubMenuContainer.classList.remove("is-flex");
  };

  /**
   * Normalizes the position of the context menu.
   * @param {number} mouseX - The X-coordinate of the mouse position.
   * @param {number} mouseY - The Y-coordinate of the mouse position.
   * @param {HTMLElement} contextMenu - The context menu element.
   * @returns {Object} - The normalized X and Y coordinates.
   */
  normalizePosition = (mouseX, mouseY, contextMenu) => {
    // compute what is the mouse position relative to the container element (scope)
    if (!contextMenu) return {};
    let { left: scopeOffsetX, top: scopeOffsetY } =
      document.body.getBoundingClientRect();

    scopeOffsetX = scopeOffsetX < 0 ? 0 : scopeOffsetX;
    scopeOffsetY = scopeOffsetY < 0 ? 0 : scopeOffsetY;

    const scopeX = mouseX - scopeOffsetX;
    const scopeY = mouseY - scopeOffsetY;

    const outOfBoundsOnX =
      scopeX + contextMenu.clientWidth > document.body.clientWidth;

    const outOfBoundsOnY =
      scopeY + contextMenu.clientHeight > document.body.clientHeight;

    let normalizedX = mouseX;
    let normalizedY = mouseY;

    if (outOfBoundsOnX) {
      normalizedX =
        scopeOffsetX + document.body.clientWidth - contextMenu.clientWidth;
    }

    if (outOfBoundsOnY) {
      normalizedY =
        scopeOffsetY + document.body.clientHeight - contextMenu.clientHeight;
    }

    return { normalizedX, normalizedY };
  };

  // Options For Context Menu
  menuForCurrentItem = (item, basket) => {
    if (item) {
      const contextMenuItems = menubar.getContextMenuItems(item.privs);
      let currentPerms = (item.privs || "(read)").toLowerCase();

      const canEditInline = (item) => {
        const maxAllowedFileSizeForInlineEdit =
          utils.getUserCustomizationValue(
            "maxAllowedFileSizeForInlineEdit",
            true
          ) || 1024;

        const allowedFileExtensionsForInlineEdit =
          utils.getUserCustomizationValue(
            "allowedFileExtensionsForInlineEdit",
            true
          ) || editInlineFilesExtensionAllowed;

        const sizeAllowed = parseInt(maxAllowedFileSizeForInlineEdit * 1024);

        const itemSize = parseInt(item.size);
        const extension = utils.getFileExtension(item);

        if (
          allowedFileExtensionsForInlineEdit.split(",").indexOf(extension) >=
            0 &&
          itemSize <= sizeAllowed
        ) {
          return true;
        }

        return false;
      };

      if (
        (!item.preview ||
          item.preview === 0 ||
          !item.mediaInfo.type === mediaTypes.VIDEO ||
          !item.mediaInfo.type === mediaTypes.AUDIO) &&
        !slideShowAllowedFilesExtensions.includes(utils.getFileExtension(item))
      ) {
        currentPerms = currentPerms.replace("(slideshow)", "");
      }

      const findMatch = (buttons) => {
        const matchedButtons = [];
        buttons.forEach((btn) => {
          const curBtn = { ...btn };

          if (
            curBtn.key === "(write):Edit file (for context menu only)" &&
            !canEditInline(item)
          ) {
            return;
          }

          if (
            curBtn.key === "(delete):Delete" &&
            item.type === "DIR" &&
            !item.privs.includes("deletedir")
          ) {
            return;
          }

          if (
            item.type !== fileTypes.DIR &&
            curBtn.key === "(subscribeToEvents):Subscribe To Events"
          ) {
            return;
          }

          if (
            item?.vfs_protocol?.toUpperCase() === "S3" &&
            curBtn.key.toString().indexOf("rename") !== -1 &&
            item.type === fileTypes.DIR
          ) {
            return;
          }

          if (
            !currentPerms ||
            !curBtn ||
            !curBtn.requiredPriv ||
            currentPerms.indexOf(curBtn.requiredPriv) >= 0 ||
            curBtn.key.toString().indexOf("(custom):") == 0
          ) {
            if (curBtn.subMenu) {
              curBtn.subMenu = findMatch(curBtn.subMenu);
            }
            matchedButtons.push({ ...curBtn });
          }
        });

        return matchedButtons;
      };

      const filterBasketItems = (buttons) => {
        const matchedButtons = [];
        buttons.forEach((btn) => {
          const curBtn = { ...btn };
          const command = curBtn.command;
          let skip;
          if (command) {
            buttonsToIgnoreForBasket.forEach((nameToIgnore) => {
              if (
                command
                  .toLowerCase()
                  .indexOf(nameToIgnore.toLowerCase().trim()) >= 0
              )
                skip = true;
            });
          }
          if (!skip) matchedButtons.push({ ...curBtn });
        });
        return matchedButtons;
      };

      return basket
        ? filterBasketItems(findMatch(contextMenuItems))
        : findMatch(contextMenuItems);
    }

    return [];
  };
}
